import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check, ArrowRight, ShieldCheck, Loader2, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { pricingPlans } from '../mock';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { trackEvent } from '../hooks/useAnalytics';

const ChoosePlan = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(null);

  const subscribe = async (planId) => {
    setLoading(planId);
    trackEvent('checkout_start', { label: planId });
    try {
      const { data } = await api.post('/billing/create-checkout-session', {
        planId,
        originUrl: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setLoading(null);
      toast({
        title: 'Could not start checkout',
        description: err?.response?.data?.detail || 'Please try again.',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-rose-50 to-amber-50 px-5 py-10 lg:py-16">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center shadow-md">
              <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-slate-900">MyBusinessAdPro</span>
          </div>
          {user && (
            <button onClick={() => { logout(); navigate('/'); }} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          )}
        </div>

        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1.5 rounded-full mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> 7-DAY FREE TRIAL · CANCEL ANYTIME
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
            {user?.name ? `Welcome, ${user.name.split(' ')[0]}!` : 'Pick your plan'}
          </h1>
          <p className="mt-3 text-lg text-slate-600">
            Try it free for 7 days. You won't be charged until day 8. Cancel anytime from your dashboard — no questions asked.
          </p>
        </div>

        {/* Plans */}
        <div className="mt-12 grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {pricingPlans.map(p => (
            <div key={p.id} className={`relative rounded-2xl p-6 sm:p-7 border transition-all ${
              p.popular
                ? 'bg-slate-950 text-white border-slate-900 shadow-xl md:scale-105'
                : 'bg-white text-slate-900 border-slate-200'
            }`}>
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-400 to-rose-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                  MOST POPULAR
                </div>
              )}
              <div className="text-sm font-semibold uppercase tracking-wider opacity-70">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-5xl font-extrabold">£{p.price}</span>
                <span className="text-sm opacity-60">/{p.period}</span>
              </div>
              <p className={`mt-2 text-sm ${p.popular ? 'text-slate-300' : 'text-slate-600'}`}>{p.description}</p>
              <div className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${p.popular ? 'bg-white/10 text-orange-300' : 'bg-emerald-50 text-emerald-700'}`}>
                <Check className="w-3 h-3" /> 7-day free trial · £0 today
              </div>
              <Button
                onClick={() => subscribe(p.id)}
                disabled={loading !== null}
                className={`mt-5 w-full rounded-xl font-semibold h-11 ${
                  p.popular ? 'bg-orange-500 hover:bg-orange-400 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'
                }`}
              >
                {loading === p.id ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe…</>
                ) : (
                  <>Start 7-day trial <ArrowRight className="w-4 h-4 ml-1.5" /></>
                )}
              </Button>
              <ul className="mt-6 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${p.popular ? 'text-orange-400' : 'text-emerald-500'}`} />
                    <span className={p.popular ? 'text-slate-200' : 'text-slate-700'}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust */}
        <div className="mt-12 max-w-2xl mx-auto text-center">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-600">
            <div className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Secured by Stripe</div>
            <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> Cancel in 1 click</div>
            <div className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-600" /> No charge during trial</div>
          </div>
          <p className="mt-5 text-xs text-slate-500">
            Your card is saved securely with Stripe. You'll only be charged if you don't cancel before the 7-day trial ends.
            After that, it's a recurring monthly subscription — cancel anytime from your dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChoosePlan;
